/**
 * SkillDetailHero - Skill detay hero bandi
 */
import * as Icons from 'lucide-react';
import { Check, Clock, Copy, Download, type LucideIcon, MessageSquare, Package, Star, User, Zap } from 'lucide-react';
import { createElement, useMemo, useState } from 'react';
import type { Skill } from '../hooks/useSkills';

function getIcon(name?: string): LucideIcon {
  if (!name) {
    return Zap
  }
  const c = (Icons as Record<string, unknown>)[name]
  return typeof c === 'function' ? (c as LucideIcon) : Zap
}

function CopyBtn({ text }: { text: string }) {
  const [ok, setOk] = useState(false)
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 2000) }}
      style={{ display:'flex', alignItems:'center', gap:'5px', padding:'8px 14px', borderRadius:'8px', fontSize:'12px', fontWeight:600, background: ok ? 'rgba(34,197,94,.15)' : 'rgba(255,255,255,.08)', color: ok ? '#22c55e' : 'var(--text-secondary)', border:`1px solid ${ok ? 'rgba(34,197,94,.3)' : 'rgba(255,255,255,.12)'}`, cursor:'pointer' }}
    >
      {ok ? <Check size={12}/> : <Copy size={12}/>} {ok ? 'Copied!' : 'Copy CLI'}
    </button>
  )
}

interface Props {
  skill: Skill
  installing: boolean
  onAction: () => void
}

export default function SkillDetailHero({ skill, installing, onAction }: Props) {
  const accent = skill.color ?? '#3b82f6'
  const cli = `aicloud skill install ${skill.name.toLowerCase().replace(/\s+/g,'_')}`

  // Icon component'i useMemo ile cache'le
  const iconElement = useMemo(() => {
    const IconComp = getIcon(skill.icon_name)
    return createElement(IconComp, { size: 34, color: accent })
  }, [skill.icon_name, accent])

  return (
    <div style={{ borderRadius:'16px', padding:'28px', background:`linear-gradient(135deg,${accent}18,${accent}06)`, border:`1px solid ${accent}28`, marginBottom:'20px' }}>
      <div style={{ display:'flex', gap:'20px', alignItems:'flex-start', flexWrap:'wrap' }}>

        {/* Icon */}
        <div style={{ width:'68px', height:'68px', borderRadius:'16px', flexShrink:0, background:`${accent}20`, border:`2px solid ${accent}40`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {iconElement}
        </div>

        {/* Meta */}
        <div style={{ flex:1, minWidth:'200px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'6px' }}>
            <h1 style={{ fontSize:'22px', fontWeight:800, margin:0 }}>{skill.name}</h1>
            <span style={{ padding:'2px 9px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:`${accent}20`, color:accent, border:`1px solid ${accent}40` }}>v{skill.version}</span>
            {skill.is_new && <span style={{ padding:'2px 9px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:'rgba(34,197,94,.15)', color:'#22c55e', border:'1px solid rgba(34,197,94,.3)' }}>NEW</span>}
            {skill.featured && <span style={{ padding:'2px 9px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:'rgba(245,158,11,.15)', color:'#f59e0b', border:'1px solid rgba(245,158,11,.3)' }}>Featured</span>}
          </div>

          <p style={{ fontSize:'14px', color:'var(--text-secondary)', margin:'0 0 12px', lineHeight:'1.55' }}>{skill.description}</p>

          <div style={{ display:'flex', alignItems:'center', gap:'14px', flexWrap:'wrap' }}>
            {/* Stars */}
            <span style={{ display:'flex', alignItems:'center', gap:'2px' }}>
              {[1,2,3,4,5].map(i=>(
                <Star key={i} size={12} fill={i<=Math.round(skill.rating)?'#f59e0b':'none'} color={i<=Math.round(skill.rating)?'#f59e0b':'#555'}/>
              ))}
              <span style={{ fontSize:'12px', color:'var(--text-secondary)', marginLeft:'4px' }}>{skill.rating.toFixed(1)}</span>
            </span>
            <span style={{ fontSize:'12px', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:'3px' }}><Download size={11}/> {skill.downloads.toLocaleString()}</span>
            <span style={{ fontSize:'12px', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:'3px' }}><MessageSquare size={11}/> {skill.discussion_count ?? 0}</span>
            <span style={{ fontSize:'12px', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:'3px' }}><User size={11}/> {skill.author}</span>
            <span style={{ padding:'2px 9px', borderRadius:'20px', fontSize:'11px', fontWeight:600, background:`${accent}15`, color:accent, border:`1px solid ${accent}30` }}>{skill.category}</span>
          </div>

          {skill.tags?.length > 0 && (
            <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', marginTop:'10px' }}>
              {skill.tags.map(t=>(
                <span key={t} style={{ fontSize:'11px', padding:'2px 7px', borderRadius:'4px', background:'rgba(255,255,255,.06)', color:'var(--text-secondary)', border:'1px solid rgba(255,255,255,.1)' }}>#{t}</span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display:'flex', flexDirection:'column', gap:'8px', flexShrink:0 }}>
          <button
            onClick={onAction}
            disabled={installing}
            style={{
              padding:'10px 22px', borderRadius:'10px', fontSize:'14px', fontWeight:700, cursor: installing ? 'not-allowed' : 'pointer', opacity: installing ? .7 : 1, transition:'all .2s',
              display:'flex', alignItems:'center', gap:'6px',
              background: skill.is_installed ? (skill.enabled ? 'rgba(239,68,68,.15)' : `${accent}20`) : accent,
              color: skill.is_installed ? (skill.enabled ? '#ef4444' : accent) : '#fff',
              border: skill.is_installed ? `1px solid ${skill.enabled ? 'rgba(239,68,68,.3)' : `${accent}40`}` : 'none'
            }}
          >
            {installing ? <><Clock size={14}/> Processing...</> : skill.is_installed ? (skill.enabled ? <><Check size={14}/> Disable</> : <><Zap size={14}/> Enable</>) : <><Package size={14}/> Install</>}
          </button>
          <CopyBtn text={cli}/>
        </div>
      </div>
    </div>
  )
}
